import { useCallback, useEffect, useState } from 'react'
import { getFaqStatus } from '../../api/faq'

export const FAST_INTERVAL_MS = 3000
export const FAST_PHASE_MS = 60000
export const SLOW_INTERVAL_MS = 10000
export const MAX_POLLING_MS = 10 * 60 * 1000

export function isAllSynced(status) {
  return !status || status.pending + status.syncing + status.failed === 0
}

// The sync counts of the tenant's FAQ, polled while entries are still
// syncing: every 3 s for the first minute after `notifyWrite()` (or the first
// load), then every 10 s. Polling stops when everything is synced, after 10
// minutes, on unmount, and pauses while the tab is hidden.
export default function useFaqStatus(tenantId) {
  const [loaded, setLoaded] = useState({ tenantId: null, status: null, error: null })
  const [writes, setWrites] = useState(0)

  useEffect(() => {
    if (!tenantId) return undefined
    const startedAt = Date.now()
    let alive = true
    let timer = null
    let inFlight = false
    let paused = false // a poll was due while the tab was hidden

    async function tick() {
      timer = null
      if (document.hidden) {
        paused = true
        return
      }
      inFlight = true
      let status = null
      try {
        status = await getFaqStatus(tenantId)
        if (alive) setLoaded({ tenantId, status, error: null })
      } catch (error) {
        if (alive) {
          setLoaded((previous) => ({ tenantId, status: previous.tenantId === tenantId ? previous.status : null, error }))
        }
      }
      inFlight = false
      if (!alive) return
      const elapsed = Date.now() - startedAt
      if ((status && isAllSynced(status)) || elapsed >= MAX_POLLING_MS) return // polling is over
      timer = setTimeout(tick, elapsed < FAST_PHASE_MS ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS)
    }

    // Only a poll that was skipped because the tab was hidden is resumed: not
    // one that is in flight, and not a polling that already ended.
    const onVisibility = () => {
      if (document.hidden || !paused || inFlight || timer !== null) return
      paused = false
      tick()
    }
    document.addEventListener('visibilitychange', onVisibility)
    tick()
    return () => {
      alive = false
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [tenantId, writes])

  // Call after any write, or to try again after an error: the fast phase starts again.
  const notifyWrite = useCallback(() => setWrites((count) => count + 1), [])

  const current = loaded.tenantId === tenantId ? loaded : { status: null, error: null }
  return { status: current.status, error: current.error, notifyWrite }
}
